import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import MapPreview from 'client/components/MapPreview/MapPreview';
import WorkpackMapInfo from 'client/pages/MapPage/components/MapInfo/WorkpackMapInfo';
import Button from 'client/components/aria/Button/Button';
import WarningText from 'client/components/aria/WarningText/WarningText';
import styles from './WorkpackPrintPreview.module.scss';
import Hint from 'client/components/aria/Form/components/Hint/Hint';
import PaperEffect from './PaperEffect';
import {
    parseOrientation,
    getPaperPreviewDimensions,
    getMapPreviewDimensions,
    getEffectiveScale,
    checkAoiCutoff,
    CONTAINER_DIMENSIONS
} from './workpackPrintPreviewUtils';

/**
 * Preview showing how the PDF will appear with selected paper size and map scale
 */
const WorkpackPrintPreview = ({ orientation, scale, aoi, featuresByAssetType, onEditArea, variant = 'default', ...props }) => {
    const intl = useIntl();
    const isSummaryVariant = variant === 'summary';

    const { paperSize, paperOrientation } = parseOrientation(orientation);
    const paperDimensions = getPaperPreviewDimensions(paperSize);
    const mapDimensions = getMapPreviewDimensions(paperSize);

    const hasOptionsSelected = orientation && scale;
    const effectiveScale = hasOptionsSelected ? getEffectiveScale(scale, paperSize) : scale;
    const isAoiCutOff = checkAoiCutoff(aoi, scale, paperSize);

    return (
        <div className={`${styles.printPreviewWrapper} ${isSummaryVariant ? styles.summaryVariant : ''}`} data-testid={props['data-testid']}>
            {!isSummaryVariant && (
                <div className={styles.headerBanner}>
                    <h3 className="govuk-heading-s govuk-!-margin-bottom-0">
                        {intl.formatMessage({ id: 'workpackPrintPreview.title' })}
                    </h3>
                </div>
            )}

            <div className={styles.contentWrapper}>
                <div className={styles.previewSection}>
                    <div className={styles.mapPreviewContainer}>
                        <div
                            className={styles.previewContainer}
                            style={{
                                width: `${CONTAINER_DIMENSIONS.width}px`,
                                height: `${CONTAINER_DIMENSIONS.height}px`
                            }}
                        >
                            <div
                                className={styles.paperEffectWrapper}
                                style={{
                                    width: `${paperDimensions.width}px`,
                                    height: `${paperDimensions.height}px`
                                }}
                            >
                                <PaperEffect
                                    className={styles.paperEffect}
                                    width={paperDimensions.width}
                                    height={paperDimensions.height}
                                />

                                {hasOptionsSelected && (
                                    <span className={`${styles.paperLabel} govuk-body-s govuk-!-font-weight-bold`}>
                                        {paperSize}
                                    </span>
                                )}

                                <div className={styles.paperContent}>
                                    {hasOptionsSelected
                                        ? (
                                            <div className={styles.previewWrapper}>
                                                <div className={styles.mapContainer}>
                                                    <MapPreview
                                                        aoi={aoi}
                                                        featuresByAssetType={featuresByAssetType}
                                                        disableInteraction={true}
                                                        scale={effectiveScale}
                                                        width={mapDimensions.width}
                                                        height={mapDimensions.height}
                                                        forceLightMode={true}
                                                    />
                                                    <div
                                                        className={`${styles.paperBoundary} ${styles.activeBoundary}`}
                                                        style={{
                                                            width: `${mapDimensions.width}px`,
                                                            height: `${mapDimensions.height}px`
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )
                                        : (
                                            <div className={styles.placeholderText}>
                                                <Hint>
                                                    {intl.formatMessage({ id: 'workpackPrintPreview.selectOptionsPlaceholder' })}
                                                </Hint>
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.infoSection}>
                    <div className={styles.infoPanel}>
                        <div className={styles.mapInfoContent}>
                            <WorkpackMapInfo aoi={aoi} />
                            {!isSummaryVariant && (
                                <div className={styles.pdfInfoSection}>
                                    {orientation && (
                                        <p className="govuk-body-s">
                                            <span className="govuk-!-font-weight-bold" data-testid="printPreview.paperSize.label">
                                                {intl.formatMessage({ id: 'workpackPrintPreview.paperSize' })}
                                            </span>
                                            {' '}
                                            <span className={styles.infoValue} data-testid="printPreview.paperSize.value">
                                                {paperSize} {paperOrientation}
                                            </span>
                                        </p>
                                    )}
                                    {scale && (
                                        <p className="govuk-body-s">
                                            <span className="govuk-!-font-weight-bold" data-testid="printPreview.scale.label">
                                                {intl.formatMessage({ id: 'workpackPrintPreview.scale' })}
                                            </span>
                                            {' '}
                                            <span className={isAoiCutOff ? styles.infoValueError : styles.infoValue} data-testid="printPreview.scale.value">
                                                {`1:${scale}`}
                                            </span>
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        {!isSummaryVariant && (
                            <Button
                                variant="transparent"
                                onPress={onEditArea}
                                className={styles.editButton}
                                data-testid={props['data-testid'] ? `${props['data-testid']}.editArea` : 'editAreaButton'}
                            >
                                {intl.formatMessage({ id: 'workpackPrintPreview.editArea' })}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {!isSummaryVariant && isAoiCutOff && (
                <div className={`${styles.warningContainer} govuk-tag--blue`}>
                    <WarningText
                        message={intl.formatMessage({ id: 'workpackPrintPreview.aoiCutoffWarning' })}
                        variant="exclamation"
                        bold={false}
                    />
                </div>
            )}
        </div>
    );
};

WorkpackPrintPreview.propTypes = {
    orientation: PropTypes.string,
    scale: PropTypes.string,
    aoi: PropTypes.object.isRequired,
    featuresByAssetType: PropTypes.object,
    onEditArea: PropTypes.func,
    variant: PropTypes.oneOf(['default', 'summary']),
    'data-testid': PropTypes.string
};

export default WorkpackPrintPreview;
